import {
  useGetDesktopDownloadLinkQuery,
  useGetOrganizationQuery,
} from '@carboncopyco/data';
import { InfoOutlineIcon } from '@carboncopyco/icons';
import { MarkBlueIllustration } from '@carboncopyco/illustrations';
import {
  Button,
  ColorHex,
  InlineLink,
  Separator,
  TextBase,
  TextCaption,
  TextPageHeader,
  Tooltip,
} from '@carboncopyco/ui';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export type RedirectPageProps = {
  redirectUrl?: string;
  resource?: 'tour' | 'demo';
  isLoginFlow?: boolean;
  appDeeplink: string;
  redirectCallback?: (string) => void;
  continueInBrowserCallback?: () => void;
  shouldShowOnlyOpenApp?: boolean;
};

export const RedirectPage = ({
  appDeeplink,
  isLoginFlow,
  redirectUrl = '/',
  resource = 'demo',
  shouldShowOnlyOpenApp = false,
  redirectCallback = (appDeeplink: string) =>
    window.location.assign(appDeeplink),
  continueInBrowserCallback,
}: RedirectPageProps) => {
  const flags = useFlags();
  const downloadRef = useRef<HTMLDivElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const { data: organization } = useGetOrganizationQuery({
    id: String(organizationId),
  });
  const { data: macOSDownloadLink, isLoading: isLoadingMacOSLink } =
    useGetDesktopDownloadLinkQuery({ platform: 'mac' });
  const { data: windowsDownloadLink, isLoading: isLoadingWindowsLink } =
    useGetDesktopDownloadLinkQuery({
      platform: 'windows',
    });

  const isLoading = isLoadingMacOSLink || isLoadingWindowsLink;
  const isPoc = organization?.state === 'poc';
  const isMac = navigator.userAgent.includes('Macintosh');
  const isWindows = navigator.userAgent.includes('Windows');

  useEffect(() => {
    if (redirectCallback) {
      redirectCallback(appDeeplink);
    }
  }, []);

  const downloadLink = useMemo(() => {
    if (isMac) {
      return macOSDownloadLink?.downloadLink;
    }
    if (isWindows) {
      return windowsDownloadLink?.downloadLink;
    }
    return undefined;
  }, [isMac, isWindows, macOSDownloadLink, windowsDownloadLink]);

  const handleDownloadDesktopApp = () => {
    if (!downloadLink) {
      return;
    }
    const anchor = document.createElement('a');
    anchor.href = downloadLink;
    document.body.appendChild(anchor);
    anchor.click();

    document.body.removeChild(anchor);
  };

  const getTitle = () => {
    if (isPoc) {
      return 'Start your Demostack trial ';
    }
    if (isLoginFlow) {
      return 'Redirecting you to Demostack app';
    }
    return 'Open the Demostack app';
  };

  const getSubtitle = (allowBrowserFallback: boolean) => {
    if (isPoc) {
      return 'Download the desktop app and start showcasing your product in the best light!';
    }
    if (isLoginFlow && allowBrowserFallback) {
      return 'Click open the app or continue in your browser.';
    }
    return 'To complete this task, you’ll need to use the desktop app.';
  };

  const Footer = () => {
    if (isLoginFlow) {
      return (
        <div>
          <TextBase
            label="If you can't download the app,"
            color="text-ds-natural-600"
          />
          <InlineLink
            label="click here"
            className="mx-1 inline-flex cursor-pointer"
            target="_blank"
            disabled={!downloadLink}
            onClick={handleDownloadDesktopApp}
          />
          <TextBase
            label="to download"
            color="text-ds-natural-600"
          />
        </div>
      );
    }
    return (
      <div>
        <TextCaption
          label="Why did I get this message?"
          color="text-ds-natural-600"
        />
        <Tooltip
          anchorRef={infoRef}
          placement="bottom"
          offset={[0, 10]}
          noArrow
        >
          <div className="w-60 px-3 py-2 shadow-ds-md rounded leading-4">
            <TextCaption
              label={`When a ${resource} is created from the desktop app, some actions can only be completed from there.`}
            />
          </div>
        </Tooltip>
      </div>
    );
  };

  return (
    <div className="h-screen flex flex-col justify-center items-center">
      <MarkBlueIllustration />
      <div className="mt-8 mb-3">
        <TextPageHeader
          label={getTitle()}
          color="text-ds-natural-700"
        />
      </div>
      <div className="mb-5">
        <TextBase
          label={getSubtitle(flags.desktopRedirectionBrowserFallback)}
          color="text-ds-natural-600"
        />
      </div>
      <div
        className={classNames(
          'flex flex-col items-center mt-2.5',
          'gap-5  mb-8'
        )}
      >
        {!shouldShowOnlyOpenApp && isPoc && resource !== 'tour' && (
          <div ref={downloadRef}>
            <Button
              disabled={(!isMac && !isWindows) || isLoading || !downloadLink}
              label="Download the app"
              onClick={handleDownloadDesktopApp}
            />
          </div>
        )}
        {appDeeplink && (
          <>
            <Button
              label="Open the app"
              link={isPoc}
              onClick={() => {
                if (redirectCallback) {
                  redirectCallback(appDeeplink);
                }
              }}
            />
            {!shouldShowOnlyOpenApp && !isPoc && (
              <div ref={downloadRef}>
                <Button
                  link
                  disabled={
                    (!isMac && !isWindows) ||
                    isLoading ||
                    (!isLoginFlow && !downloadLink)
                  }
                  label={
                    isLoginFlow
                      ? 'Continue in your browser'
                      : 'Download the app'
                  }
                  onClick={() => {
                    if (isLoginFlow) {
                      if (continueInBrowserCallback) {
                        continueInBrowserCallback();
                      } else {
                        navigate(redirectUrl);
                      }
                    } else {
                      handleDownloadDesktopApp();
                    }
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
      {!isPoc && (
        <>
          <div className="w-[407px] flex justify-center">
            <Separator
              horizontal
              middle
            />
          </div>
          <div
            className="flex items-center mt-5 gap-1"
            ref={infoRef}
          >
            <InfoOutlineIcon
              size={12}
              color={ColorHex.natural600}
            />
            <Footer />
          </div>
        </>
      )}
    </div>
  );
};
